import { useEffect, useMemo, useState } from 'react';
import { Modal, Form, Row, Col, Alert } from 'react-bootstrap';
import { postCompanyPorts, putCompanyPorts } from '../../apiCalls';

const DUPLICATE_MESSAGE = 'Duplicate Port Code for selected route and port';

const PortsModal = ({ show, port, directions, existingPorts, onHide, onSaved, onError }) => {
  const [localPorts, setLocalPorts] = useState({
    id: -1,
    portName: '',
    portType: '',
    exitPort: '',
    EntryPort: '',
    portProcess: '',
    custOffCode: '',
    GVMSGoodsLocation: '',
    InvLinkedGoodsLocation: '',
    AltGoodsLocation: '',
  });
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (show) {
      setError('');
    }
  }, [show]);

  useEffect(() => {
    if (port) {
      setLocalPorts({
        id: port.id ?? -1,
        portName: port.portName ?? '',
        portType: port.portType ?? '',
        exitPort: port.exitPort ?? '',
        EntryPort: port.EntryPort ?? '',
        portProcess: port.portProcess ?? '',
        custOffCode: port.custOffCode ?? '',
        GVMSGoodsLocation: port.GVMSGoodsLocation ?? '',
        InvLinkedGoodsLocation: port.InvLinkedGoodsLocation ?? '',
        AltGoodsLocation: port.AltGoodsLocation ?? '',
      });
    } else {
      setLocalPorts({
        id: -1,
        portName: '',
        portType: '',
        exitPort: '',
        EntryPort: '',
        portProcess: '',
        custOffCode: '',
        GVMSGoodsLocation: '',
        InvLinkedGoodsLocation: '',
        AltGoodsLocation: '',
      });
    }
  }, [port]);

  const directionOptions = useMemo(() => directions || [], [directions]);

  const isExitTwo = Number(localPorts.exitPort) === 2;
  const isExitTwelve = Number(localPorts.exitPort) === 12;
  const isEntryThree = Number(localPorts.EntryPort) === 3;
  const isEntryThirteen = Number(localPorts.EntryPort) === 13;

  const disableEntry =
    isExitTwo || (!!localPorts.exitPort && Number(localPorts.exitPort) !== 12 && Number(localPorts.exitPort) !== 2);
  const disableExit = !!localPorts.EntryPort && !isEntryThree && !isEntryThirteen;

  const filteredEntryOptions = useMemo(() => {
    if (isExitTwelve) {
      return directionOptions.filter((d) => Number(d.id) === 13);
    }
    return directionOptions;
  }, [directionOptions, isExitTwelve]);

  const handleExitChange = (val) => {
    const exitVal = val ? Number(val) : '';
    if (exitVal === 2) {
      setLocalPorts((prev) => ({ ...prev, exitPort: exitVal, EntryPort: 3 }));
    } else if (exitVal === 12) {
      setLocalPorts((prev) => ({ ...prev, exitPort: exitVal, EntryPort: prev.EntryPort === 13 ? 13 : '' }));
    } else {
      setLocalPorts((prev) => ({ ...prev, exitPort: exitVal, EntryPort: '' }));
    }
  };

  const handleEntryChange = (val) => {
    const entryVal = val ? Number(val) : '';
    if (entryVal && entryVal !== 3 && entryVal !== 13) {
      // Any other entry disables exit selection
      setLocalPorts((prev) => ({ ...prev, EntryPort: entryVal, exitPort: '' }));
    } else if (entryVal === 3 && isExitTwo) {
      setLocalPorts((prev) => ({ ...prev, EntryPort: 3, exitPort: 2 }));
    } else {
      setLocalPorts((prev) => ({ ...prev, EntryPort: entryVal }));
    }
  };

  const combinationExists = (candidate) => {
    const key = `${(candidate.portName || '').trim().toLowerCase()}|${(candidate.portProcess || '')
      .trim()
      .toLowerCase()}|${candidate.exitPort || ''}|${candidate.EntryPort || ''}`;
    return existingPorts.some((p) => {
      const compareKey = `${(p.portName || '').trim().toLowerCase()}|${(p.portProcess || '').trim().toLowerCase()}|${
        p.exitPort || ''
      }|${p.EntryPort || ''}`;
      return compareKey === key && p.id !== candidate.id;
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    // Map PascalCase from API/state to camelCase for backend
    const payload = {
      id: localPorts.id,
      portName: localPorts.portName,
      portType: localPorts.portType,
      exitPort: localPorts.exitPort ? Number(localPorts.exitPort) : '',
      EntryPort: localPorts.EntryPort ? Number(localPorts.EntryPort) : '',
      portProcess: localPorts.portProcess,
      custOffCode: localPorts.custOffCode || '',
      gvmsGoodsLocation: localPorts.GVMSGoodsLocation || '',
      invGoodsLocation: localPorts.InvLinkedGoodsLocation || '',
      altGoodsLocation: localPorts.AltGoodsLocation || '',
    };

    // Enforce rules before save
    if (payload.exitPort === 2) {
      payload.EntryPort = 3;
    } else if (payload.exitPort === 12) {
      if (payload.EntryPort && payload.EntryPort !== 13) {
        setError('When Exit Port is 12, only Entry Port 13 is allowed.');
        return;
      }
    } else if (payload.exitPort && payload.exitPort !== 2 && payload.exitPort !== 12) {
      payload.EntryPort = '';
    }

    if (payload.EntryPort && payload.EntryPort !== 3 && payload.EntryPort !== 13) {
      payload.exitPort = '';
    }

    if (combinationExists(payload)) {
      setError(DUPLICATE_MESSAGE);
      onError?.(DUPLICATE_MESSAGE);
      return;
    }

    try {
      setError('');
      setSaving(true);
      const isDuplicateResponse = (result) =>
        Array.isArray(result) && result[0]?.completed === 'Duplicate record found';

      if (payload.id === -1) {
        const result = await postCompanyPorts(payload);
        if (isDuplicateResponse(result)) {
          setError(DUPLICATE_MESSAGE);
          onError?.(DUPLICATE_MESSAGE);
          return;
        }
        onSaved?.(true);
      } else {
        const result = await putCompanyPorts(payload);
        if (isDuplicateResponse(result)) {
          setError(DUPLICATE_MESSAGE);
          onError?.(DUPLICATE_MESSAGE);
          return;
        }
        onSaved?.(false);
      }
    } catch (err) {
      console.error(err);
      setError('Error saving Port Code');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal show={show} onHide={onHide} centered size="lg" contentClassName="editModal" dialogClassName="wide-modal">
      <Modal.Header>
        <Modal.Title>{localPorts?.id === -1 ? 'Add Port Code' : `Edit: ${localPorts?.portName || ''}`}</Modal.Title>
        <button type="button" className="btn-close" aria-label="Close" onClick={onHide} />
      </Modal.Header>
      <Form onSubmit={handleSubmit} className="flex-grow-1 d-flex flex-column">
        <Modal.Body className="flex-grow-1">
          {error && (
            <Alert variant="danger" className="mb-2">
              {error}
            </Alert>
          )}
          <Row className="mb-2">
            <Col>
              <Form.Group className="mb-2">
                <Form.Label>Port Name</Form.Label>
                <Form.Control
                  name="portName"
                  type="text"
                  required
                  value={localPorts.portName}
                  onChange={(e) => setLocalPorts((prev) => ({ ...prev, portName: e.target.value }))}
                />
              </Form.Group>
            </Col>
            <Col>
              <Form.Group className="mb-2">
                <Form.Label>Port Type</Form.Label>
                <Form.Control
                  name="portType"
                  type="text"
                  value={localPorts.portType}
                  onChange={(e) => setLocalPorts((prev) => ({ ...prev, portType: e.target.value }))}
                />
              </Form.Group>
            </Col>
          </Row>
          <Row className="mb-2">
            <Col>
              <Form.Group className="mb-2">
                <Form.Label>Exit Port</Form.Label>
                <Form.Control
                  as="select"
                  name="exitPort"
                  value={localPorts.exitPort}
                  onChange={(e) => handleExitChange(e.target.value)}
                  disabled={disableExit}
                >
                  <option value="">-- Select Exit Port --</option>
                  {directionOptions.map((opt) => (
                    <option key={opt.id} value={opt.id}>
                      {opt.data}
                    </option>
                  ))}
                </Form.Control>
                {isExitTwo && <small className="text-muted">Entry Port will be set to 3 automatically.</small>}
                {disableExit && !isEntryThree && !isEntryThirteen && (
                  <small className="text-muted">Exit Port disabled when Entry Port is not 3 or 13.</small>
                )}
              </Form.Group>
            </Col>
            <Col>
              <Form.Group className="mb-2">
                <Form.Label>Entry Port</Form.Label>
                <Form.Control
                  as="select"
                  name="EntryPort"
                  value={localPorts.EntryPort}
                  onChange={(e) => handleEntryChange(e.target.value)}
                  disabled={disableEntry}
                >
                  <option value="">-- Select Entry Port --</option>
                  {filteredEntryOptions.map((opt) => (
                    <option key={opt.id} value={opt.id}>
                      {opt.data}
                    </option>
                  ))}
                </Form.Control>
                {isExitTwelve && (
                  <small className="text-muted">Only Entry Port 13 is permitted with Exit Port 12.</small>
                )}
                {disableEntry && !isExitTwo && (
                  <small className="text-muted">Entry Port disabled when Exit Port is neither 2 nor 12.</small>
                )}
              </Form.Group>
            </Col>
          </Row>
          <Row className="mb-2">
            <Col>
              <Form.Group className="mb-2">
                <Form.Label>Route / Port Process</Form.Label>
                <Form.Control
                  name="portProcess"
                  type="text"
                  value={localPorts.portProcess}
                  onChange={(e) => setLocalPorts((prev) => ({ ...prev, portProcess: e.target.value }))}
                />
              </Form.Group>
            </Col>
          </Row>
          <Row className="mb-2">
            <Col>
              <Form.Group className="mb-2">
                <Form.Label>Customs Office Code</Form.Label>
                <Form.Control
                  name="custOffCode"
                  type="text"
                  value={localPorts.custOffCode}
                  onChange={(e) => setLocalPorts((prev) => ({ ...prev, custOffCode: e.target.value }))}
                />
              </Form.Group>
            </Col>
            <Col>
              <Form.Group className="mb-2">
                <Form.Label>GVMS Goods Location</Form.Label>
                <Form.Control
                  name="GVMSGoodsLocation"
                  type="text"
                  value={localPorts.GVMSGoodsLocation}
                  onChange={(e) => setLocalPorts((prev) => ({ ...prev, GVMSGoodsLocation: e.target.value }))}
                />
              </Form.Group>
            </Col>
          </Row>
          <Row className="mb-2">
            <Col>
              <Form.Group className="mb-2">
                <Form.Label>INV Goods Location</Form.Label>
                <Form.Control
                  name="InvLinkedGoodsLocation"
                  type="text"
                  value={localPorts.InvLinkedGoodsLocation}
                  onChange={(e) => setLocalPorts((prev) => ({ ...prev, InvLinkedGoodsLocation: e.target.value }))}
                />
              </Form.Group>
            </Col>
            <Col>
              <Form.Group className="mb-2">
                <Form.Label>ALT Goods Location</Form.Label>
                <Form.Control
                  name="AltGoodsLocation"
                  type="text"
                  value={localPorts.AltGoodsLocation}
                  onChange={(e) => setLocalPorts((prev) => ({ ...prev, AltGoodsLocation: e.target.value }))}
                />
              </Form.Group>
            </Col>
          </Row>
        </Modal.Body>
        <Modal.Footer
          className="d-flex flex-row py-3 px-3 gap-3 border-top bg-light justify-content-end align-items-center"
          style={{ borderBottomLeftRadius: '20px', borderBottomRightRadius: '20px' }}
        >
          <button
            type="button"
            className="cancel-button"
            onClick={onHide}
            style={{ display: 'flex', borderRadius: '10px' }}
            disabled={saving}
          >
            Cancel
          </button>
          <button
            type="submit"
            className="blue-button-teams green-btn"
            style={{ borderRadius: '10px' }}
            disabled={saving}
          >
            {saving ? 'Saving...' : 'Save'}
          </button>
        </Modal.Footer>
      </Form>
    </Modal>
  );
};

export default PortsModal;
